export type BattleAgentMode = 'PUBLIC' | 'PRIVATE';
export type BattleAgentOddsStrategy = 'RANDOM' | 'FAVORITE' | 'UNDERDOG';
export type BattleAgentStakeMode = 'RANGE' | 'TARGET_AVERAGE';
export type BattleAgentMatchPopularityCriterion = 'BETS' | 'VIEWS' | 'INTEREST';
export type BattleAgentMatchSortBy = 'BETS' | 'VIEWS' | 'INTEREST' | 'RANDOM';
export type BattleAgentInviteTagMode = 'ALL' | 'ANY';
export type BattleAgentMaxActiveScope = 'BOTS' | 'ALL';
export type BattleAgentMaxActiveVisibility = 'BOTH' | 'PUBLIC' | 'PRIVATE';
export type BattleAgentCreatorSort =
    | 'LAST_ACTIVE_ASC'
    | 'LAST_ACTIVE_DESC'
    | 'BALANCE_DESC'
    | 'ACCOUNT_AGE_ASC'
    | 'ACCOUNT_AGE_DESC';
export type BattleAgentRunStatus = 'SUCCESS' | 'SKIPPED' | 'FAILED';

export interface BattleAgentsSettingsDto {
    enabled: boolean;
    intervalMs: number;
    autoAcceptEnabled: boolean;
    autoAcceptMinutesBeforeKickoff: number;
    /** 0 = unlimited */
    maxActiveBattles: number;
    maxActiveScope: BattleAgentMaxActiveScope;
    /** Which battles count toward the global active cap. */
    maxActiveVisibility: BattleAgentMaxActiveVisibility;
    /** Max agents that attempt create per Crons tick. Min 1. */
    maxCreatesPerTick: number;
    /** Max auto-accepts per tick. 0 = unlimited. */
    maxAutoAcceptsPerTick: number;
    activityBetsEnabled: boolean;
    activityBetsMaxPerTick: number;
    activityBetsStakeMin: number;
    activityBetsStakeMax: number;
    activityBetsCooldownHours: number;
    activityBetsHoursAhead: number;
}

export interface BattleAgentBotAgentRef {
    id: number;
    slug: string;
    name: string;
    enabled: boolean;
    stakeMin: number;
    stakeMax: number;
    maxPerDay: number;
}

export interface BattleAgentBotAccountDto {
    userId: number;
    name: string | null;
    email: string | null;
    photoUrl: string | null;
    countryIso: string | null;
    countryName: string | null;
    moneyBalance: number;
    lastVisitAt: string | null;
    agents: BattleAgentBotAgentRef[];
    weeklyEstimatedSpend: number;
    daysOfRunway: number | null;
    lowBalance: boolean;
    runwayLabel: string;
}

export interface BattleAgentBotAccountsResponse {
    items: BattleAgentBotAccountDto[];
    lowBalanceCount: number;
}

export interface BattleAgentUpsertRequest {
    slug: string;
    name: string;
    enabled: boolean;
    globalKillSwitch: boolean;
    creatorUserIds: number[];
    modes: BattleAgentMode[];
    publicShare: number;
    stakeMin: number;
    stakeMax: number;
    stakeMode: BattleAgentStakeMode;
    stakeAverageMultiplier: number;
    stakeBalanceMaxPercent: number;
    /** 0 = no rounding; positive = round stake to nearest multiple (ties up). */
    stakeRoundingStep: number;
    invitePurchasersOnly: boolean;
    sportIds: number[];
    betTypeGroupIds: number[];
    varietyParamPValues: number[];
    matchPopularityCriteria: BattleAgentMatchPopularityCriterion[];
    matchMinBets: number;
    matchMinViews: number;
    matchMinInterest: number;
    matchSortBy: BattleAgentMatchSortBy;
    oddsMin: number | null;
    oddsMax: number | null;
    oddsStrategy: BattleAgentOddsStrategy;
    hoursAhead: number;
    includeLive: boolean;
    maxPerHour: number;
    maxPerDay: number;
    /** 0 = unlimited for this agent (global cap still applies). */
    maxActiveBattles: number;
    /** Which battles count toward this agent's maxActiveBattles. */
    maxActiveVisibility: BattleAgentMaxActiveVisibility;
    /** Per-agent create cadence in seconds. 0 = use global interval. */
    intervalSeconds: number;
    cooldownMinutesPerCreator: number;
    minCreatorBalance: number;
    inviteTagSlugs: string[];
    inviteTagMode: BattleAgentInviteTagMode;
    inviteExcludeTagSlugs: string[];
    inviteExcludeUserIds: number[];
    inviteUserIds: number[];
    maxInvitesPerTargetDays: number;
    /** Same bot → same invitee cooldown in hours. 0 = off. */
    invitePairCooldownHours: number;
    creatorSort: BattleAgentCreatorSort;
    notes: string | null;
}

export interface BattleAgentDto extends BattleAgentUpsertRequest {
    id: string;
    createdAt: string;
    updatedAt: string | null;
    runsLast24h: number;
    successLast24h: number;
    lastRunStatus?: string | null;
    lastRunMessage?: string | null;
    lastRunAt?: string | null;
    lastRunChallengeId?: number | null;
}

export interface BattleAgentRunDto {
    id: string;
    agentId: string;
    agentSlug: string;
    status: BattleAgentRunStatus;
    mode: BattleAgentMode | null;
    creatorUserId: number | null;
    inviteeUserId: number | null;
    challengeId: number | null;
    grabbedBetId: number | null;
    matchId: number | null;
    moneyPlaced: number | null;
    message: string | null;
    createdAt: string;
}

export interface BattleAgentRunParticipantDto {
    userId: number;
    userName: string | null;
    isCreator: boolean;
    state: number;
    stateLabel: string;
}

export interface BattleAgentRunHistoryItemDto extends BattleAgentRunDto {
    challengeState: number | null;
    challengeStateLabel: string | null;
    opponentAccepted: boolean | null;
    acceptanceLabel: string | null;
    participants: BattleAgentRunParticipantDto[];
}

export interface BattleAgentRunHistorySummaryDto {
    successLast24h: number;
    skippedLast24h: number;
    failedLast24h: number;
    createdBattlesInResult: number;
    pendingInResult: number;
    acceptedOrStartedInResult: number;
    finishedInResult: number;
}

export interface BattleAgentRunHistoryPageDto {
    items: BattleAgentRunHistoryItemDto[];
    offset: number;
    limit: number;
    totalItems: number;
    summary: BattleAgentRunHistorySummaryDto;
}

export interface BattleAgentRunHistoryQuery {
    agentId?: string;
    creatorUserId?: string;
    inviteeUserId?: string;
    status?: string;
    mode?: string;
    onlyWithChallenge?: boolean;
    createdAfterMs?: number;
    createdBeforeMs?: number;
    offset?: number;
    limit?: number;
}

export interface BattleAgentsStatsSlice {
    created: number;
    played: number;
    pending: number;
    dead: number;
    playRate: number;
}

export interface BattleAgentsStatsWindow {
    days: number;
    total: BattleAgentsStatsSlice;
    agent: BattleAgentsStatsSlice;
    organic: BattleAgentsStatsSlice;
    botAccount: BattleAgentsStatsSlice;
    nonBotAccount: BattleAgentsStatsSlice;
}

export interface BattleAgentsStatsDay {
    date: string;
    createdTotal: number;
    createdAgent: number;
    createdOrganic: number;
    playedTotal: number;
    playedAgent: number;
    playedOrganic: number;
}

export interface BattleAgentsStatsOverview {
    windows: BattleAgentsStatsWindow[];
    dailyLast30Days: BattleAgentsStatsDay[];
    botUserIdsCount: number;
}

export interface BattleAgentCandidateUserDto {
    id: number;
    name: string | null;
    email: string | null;
    photoUrl: string | null;
    tags: string[];
    lastActiveAt: string | null;
    createdAt: string | null;
    moneyBalance: number | null;
}

export interface BattleAgentCandidateUsersResponse {
    items: BattleAgentCandidateUserDto[];
    offset: number;
    limit: number;
    totalItems: number;
}

export const emptyBattleAgentForm = (): BattleAgentUpsertRequest => ({
    slug: '',
    name: '',
    enabled: false,
    globalKillSwitch: false,
    creatorUserIds: [],
    modes: ['PUBLIC'],
    publicShare: 0.7,
    stakeMin: 500,
    stakeMax: 500,
    stakeMode: 'RANGE',
    stakeAverageMultiplier: 1,
    stakeBalanceMaxPercent: 0,
    stakeRoundingStep: 0,
    invitePurchasersOnly: false,
    sportIds: [],
    betTypeGroupIds: [],
    varietyParamPValues: [],
    matchPopularityCriteria: [],
    matchMinBets: 0,
    matchMinViews: 0,
    matchMinInterest: 0,
    matchSortBy: 'RANDOM',
    oddsMin: null,
    oddsMax: null,
    oddsStrategy: 'RANDOM',
    hoursAhead: 24,
    includeLive: false,
    maxPerHour: 5,
    maxPerDay: 30,
    maxActiveBattles: 0,
    maxActiveVisibility: 'BOTH',
    intervalSeconds: 0,
    cooldownMinutesPerCreator: 60,
    minCreatorBalance: 1000,
    inviteTagSlugs: [],
    inviteTagMode: 'ANY',
    inviteExcludeTagSlugs: [],
    inviteExcludeUserIds: [],
    inviteUserIds: [],
    maxInvitesPerTargetDays: 7,
    invitePairCooldownHours: 24,
    creatorSort: 'LAST_ACTIVE_ASC',
    notes: null,
});
